import { Stack, Card, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, NumberInput, Radio, RadioGroup } from '@repletoryreach/sdk-ui/inputs';
import { detectBrowserTimezone } from '@repletoryreach/sdk-utils/date';
import { describeInstant, nowLocalValue, scheduledInstant, type SendMode } from '../draft';
import type { StepProps } from './shared';

/**
 * Step 3: when it goes out.
 *
 * The whole point of this step is the timezone. A `datetime-local` input has
 * none: the browser reads what is typed in whatever zone the machine is set to,
 * so somebody travelling, or an agency working across borders, scheduled a
 * 09:00 message that went out at 11:00 for the shop that sent it. The old form
 * had a bare "Send at" box and said nothing about any of this.
 *
 * So the organisation's zone is named under the field, the time typed is read
 * in that zone, and the instant it comes to is written back out in words. When
 * the browser's own zone differs, that is said too, because a person reading
 * "14:30" on their own screen deserves to know whose 14:30 it is.
 */
export function WhenStep({ state, set, errors, t, timezone }: StepProps & { timezone: string }) {
  const browserZone = detectBrowserTimezone();
  const differs = browserZone !== timezone;
  const at = state.sendMode === 'schedule' ? scheduledInstant(state.sendAt, timezone) : null;

  return (
    <Stack gap="lg">
      {/*
        * `RadioGroup` is used for the fieldset and its legend only. Each radio
        * is wired by hand because sdk-ui's `Radio` does not read the group's
        * context: the provider is there, `useRadioGroupContext` is exported,
        * and no component calls it, so a `<RadioGroup value=…>` renders with
        * nothing checked and clicking an option does nothing. Left alone here
        * rather than fixed in the design system, which this work does not own.
        */}
      <RadioGroup label={t('broadcasts.new.when.label')}>
        <Stack gap={8}>
          <Radio
            name="rr-bc-send-mode"
            value="now"
            checked={state.sendMode === 'now'}
            onChange={() => set('sendMode', 'now' as SendMode)}
            label={t('broadcasts.new.when.now')}
            description={t('broadcasts.new.when.nowHint')}
          />
          <Radio
            name="rr-bc-send-mode"
            value="schedule"
            checked={state.sendMode === 'schedule'}
            onChange={() => set('sendMode', 'schedule' as SendMode)}
            label={t('broadcasts.new.when.later')}
            description={t('broadcasts.new.when.laterHint')}
          />
        </Stack>
      </RadioGroup>

      {state.sendMode === 'schedule' && (
        <Stack gap={8}>
          <TextInput
            type="datetime-local"
            label={t('broadcasts.form.sendAt')}
            // The zone, under the control, in the field's own hint: this is the
            // sentence that stops the mistake.
            description={t('broadcasts.new.when.zoneHint', { zone: timezone })}
            error={errors.sendAt}
            min={nowLocalValue(timezone)}
            value={state.sendAt}
            onChange={(e) => set('sendAt', e.currentTarget.value)}
          />

          {at && (
            <Card withBorder>
              <Stack gap={4}>
                <Text role="bodySmall" fw={600}>
                  {t('broadcasts.new.when.goesOut', { when: describeInstant(at, timezone), zone: timezone })}
                </Text>
                {differs && (
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {t('broadcasts.new.when.yourClock', {
                      when: describeInstant(at, browserZone), zone: browserZone
                    })}
                  </Text>
                )}
              </Stack>
            </Card>
          )}

          {differs && (
            <Alert color="info">{t('broadcasts.new.when.differentZone', { org: timezone, browser: browserZone })}</Alert>
          )}
        </Stack>
      )}

      <NumberInput
        label={t('broadcasts.form.throttle')}
        description={t('broadcasts.form.throttleHint')}
        value={state.throttle}
        onChange={(v) => set('throttle', typeof v === 'number' && v >= 1 && v <= 200 ? v : 60)}
        min={1}
        max={200}
      />
    </Stack>
  );
}
